/* PopupsManager — ATS recruiter panel for adding/removing custom pop-up
 * translation questions used in the 3-minute initial test.
 *
 * Collapsible card. Renders a list of existing custom popups and an inline
 * form to add new ones.  Built-in popups (PHASE3_POPUPS) are NOT shown here
 * — only the custom additions.
 */
import { useEffect, useState } from "react";
import { api } from "@/api/client";

export default function PopupsManager() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ prompt_ar: "", prompt_en: "", answers: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/ats/custom-popups");
      setItems(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر تحميل الأسئلة");
    }
  };

  useEffect(() => {
    if (!open) return;
    api.get("/ats/custom-popups")
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => setErr(e?.response?.data?.detail || "تعذّر تحميل الأسئلة"));
  }, [open]);

  const add = async () => {
    setErr("");
    if (!form.prompt_ar.trim()) { setErr("النص العربي مطلوب"); return; }
    const answers = form.answers.split(",").map((s) => s.trim()).filter(Boolean);
    if (answers.length === 0) { setErr("أضف إجابة صحيحة واحدة على الأقل (افصل بفاصلة)"); return; }
    setBusy(true);
    try {
      await api.post("/ats/custom-popups", {
        prompt_ar: form.prompt_ar.trim(),
        prompt_en: form.prompt_en.trim(),
        answers,
      });
      setForm({ prompt_ar: "", prompt_en: "", answers: "" });
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر الإضافة");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف هذا السؤال؟")) return;
    try {
      await api.delete(`/ats/custom-popups/${id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "تعذّر الحذف");
    }
  };

  const card = {
    background: "rgba(13,124,121,0.08)",
    border: "1px solid rgba(13,124,121,0.35)",
    borderRadius: 10, padding: 14, marginBottom: 14, direction: "rtl",
  };
  const header = {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    cursor: "pointer", fontWeight: 800, color: "#d4af37", fontSize: 14,
  };
  const input = {
    width: "100%", padding: "9px 12px", background: "#0a0a0f",
    border: "1px solid rgba(212,175,55,0.25)", borderRadius: 8,
    color: "#fff", fontSize: 13, outline: "none",
  };
  const label = { fontSize: 11.5, color: "#a0a0a0", fontWeight: 700, marginBottom: 4, display: "block" };

  return (
    <div style={card} data-testid="popups-manager">
      <div style={header} onClick={() => setOpen((v) => !v)} data-testid="popups-manager-toggle">
        <span>📝 إدارة أسئلة البوب أب الإضافية {items.length > 0 && <span style={{ color: "#9a9a9f", fontWeight: 600 }}>({items.length})</span>}</span>
        <span style={{ fontSize: 12 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: "#9a9a9f", marginBottom: 10, lineHeight: 1.7 }}>
            هذه أسئلة الترجمة المنبثقة التي يضيفها المدير ـ تُعرض للمرشّح أثناء الاختبار الأولي مع الأسئلة المدمجة.
            <br/>الإجابات المقبولة افصلها بفاصلة (مثال: <code style={{ color: "#d4af37" }}>businesswoman, business woman, businessmen</code>).
            <br/>المطابقة تتجاوز الفراغات والفواصل تلقائياً ـ لو كتب المرشّح <b>business woman</b> أو <b>businesswoman</b> تُحتسب صحيحة.
          </div>

          {/* List */}
          {items.length === 0
            ? <div style={{ fontSize: 12.5, color: "#9a9a9f", padding: "10px 0" }}>لا توجد أسئلة مضافة بعد.</div>
            : <div style={{ marginBottom: 12 }}>
                {items.map((p) => (
                  <div key={p.id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                    padding: "8px 10px", background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(212,175,55,0.15)", borderRadius: 8, marginBottom: 6,
                  }} data-testid={`popup-row-${p.id}`}>
                    <div style={{ flex: 1, fontSize: 12.5 }}>
                      <div style={{ color: "#fff", fontWeight: 700 }}>{p.prompt_ar}</div>
                      <div style={{ color: "#9a9a9f", fontSize: 11.5, marginTop: 2 }}>
                        الإجابات المقبولة: <span style={{ color: "#10b981" }}>{(p.answers || []).join(" · ")}</span>
                      </div>
                    </div>
                    <button onClick={() => remove(p.id)}
                            style={{ padding: "5px 10px", background: "transparent", border: "1px solid #dc2626",
                                     color: "#dc2626", borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: "pointer" }}
                            data-testid={`popup-delete-${p.id}`}>حذف</button>
                  </div>
                ))}
              </div>
          }

          {/* Add form */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={label}>النص العربي (يُعرض للمرشّح) *</label>
              <input style={input} value={form.prompt_ar}
                     onChange={(e) => setForm({ ...form, prompt_ar: e.target.value })}
                     placeholder="مثال: اكتب بالإنجليزية: محاسب"
                     data-testid="popup-form-prompt-ar" />
            </div>
            <div>
              <label style={label}>النص بالإنجليزية (اختياري)</label>
              <input style={input} value={form.prompt_en}
                     onChange={(e) => setForm({ ...form, prompt_en: e.target.value })}
                     placeholder="Type in English: محاسب"
                     data-testid="popup-form-prompt-en" />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={label}>الإجابات المقبولة (افصل بفاصلة) *</label>
              <input style={input} value={form.answers}
                     onChange={(e) => setForm({ ...form, answers: e.target.value })}
                     placeholder="accountant, acc"
                     data-testid="popup-form-answers" />
            </div>
          </div>

          {err && <div style={{ color: "#fca5a5", fontSize: 12, marginTop: 8 }} data-testid="popup-form-error">{err}</div>}

          <button onClick={add} disabled={busy}
                  style={{ marginTop: 12, padding: "9px 18px", background: "#0d7c79",
                           border: "none", color: "#fff", borderRadius: 8, fontWeight: 800,
                           cursor: busy ? "not-allowed" : "pointer", fontSize: 13, opacity: busy ? 0.7 : 1 }}
                  data-testid="popup-form-submit">
            {busy ? "جارٍ الإضافة…" : "➕ إضافة سؤال"}
          </button>
        </div>
      )}
    </div>
  );
}
